import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import AccountService from "@/services/AccountService";
import { apiProvider } from "@/services/utilities/Provider";
import { isHexColor, readableTextOn } from "@/pages/chat/hooks/brandColors";

const DEFAULT_VARS = {
    "--brand-from": "#9D00FF",
    "--brand-via": "#9000E9",
    "--brand-to": "#8500D8",
    "--brand-accent": "#a855f7",
    "--brand-on-primary": "#FFFFFF",
};

export const brandAssetUrl = (path) => (path ? `${apiProvider.BASE_URL}/${path}` : null);

const BrandingContext = createContext({ branding: null, setBranding: () => {}, reload: () => {} });

export const BrandingProvider = ({ children }) => {
    const [branding, setBranding] = useState(null);

    const reload = useCallback(() => {
        AccountService.getBranding()
            .then(setBranding)
            .catch(() => setBranding({}));
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const primary = branding?.primaryColor;
    useEffect(() => {
        const vars = isHexColor(primary)
            ? {
                "--brand-from": primary,
                "--brand-via": primary,
                "--brand-to": primary,
                "--brand-accent": primary,
                "--brand-on-primary": readableTextOn(primary),
            }
            : DEFAULT_VARS;
        const root = document.documentElement;
        Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
        return () => Object.keys(vars).forEach((k) => root.style.removeProperty(k));
    }, [primary]);

    const logoUrl = brandAssetUrl(branding?.logoUrl);
    useEffect(() => {
        if (!logoUrl) return undefined;
        let link = document.querySelector("link[rel~='icon']");
        const created = !link;
        if (created) {
            link = document.createElement("link");
            link.rel = "icon";
            document.head.appendChild(link);
        }
        const previous = link.href;
        link.href = logoUrl;
        return () => { if (created) link.remove(); else link.href = previous; };
    }, [logoUrl]);

    const value = useMemo(() => ({
        branding,
        logoUrl,
        setBranding,
        reload,
    }), [branding, logoUrl, reload]);

    return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = () => useContext(BrandingContext);
